import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, FileText } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const openModal = (type: ModalType) => setActiveModal(type);
  const closeModal = () => setActiveModal(null);
  return <>
      <footer className="py-12 px-6 bg-[#14142B] border-t border-[#F5F0E8]/5 text-center" data-id="element-31">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-6" data-id="element-32">
          <p className="text-[#F5F0E8]/40 text-sm" data-id="element-33">
            © {new Date().getFullYear()} Dormí Hoy. Todos los derechos
            reservados.
          </p>

          <div className="flex flex-wrap justify-center gap-6 text-sm" data-id="element-34">
            <button onClick={() => openModal('privacy')} className="text-[#F5F0E8]/60 hover:text-[#D4A574] transition-colors underline decoration-[#F5F0E8]/20 hover:decoration-[#D4A574]" data-id="element-35">
              Política de Privacidad
            </button>
            <button onClick={() => openModal('terms')} className="text-[#F5F0E8]/60 hover:text-[#D4A574] transition-colors underline decoration-[#F5F0E8]/20 hover:decoration-[#D4A574]" data-id="element-36">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-37">
        {activeModal && <Modal type={activeModal} onClose={closeModal} data-id="element-38" />}
      </AnimatePresence>
    </>;
}
function Modal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  const Icon = type === 'privacy' ? Shield : FileText;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-39">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-40" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-2xl bg-[#1C1C2E] rounded-2xl shadow-2xl border border-[#D4A574]/20 overflow-hidden flex flex-col max-h-[85vh]" data-id="element-41">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#F5F0E8]/10 bg-[#14142B]" data-id="element-42">
          <div className="flex items-center gap-3" data-id="element-43">
            <div className="p-2 rounded-lg bg-[#D4A574]/10" data-id="element-44">
              <Icon className="w-5 h-5 text-[#D4A574]" data-id="element-45" />
            </div>
            <h3 className="text-xl font-serif font-bold text-white" data-id="element-46">{title}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F5F0E8]/10 text-[#F5F0E8]/60 hover:text-white transition-colors" data-id="element-47">
            <X size={20} data-id="element-48" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto text-[#F5F0E8]/80 space-y-4 text-sm leading-relaxed custom-scrollbar" data-id="element-49">
          {content}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#F5F0E8]/10 bg-[#14142B] flex justify-end" data-id="element-50">
          <button onClick={onClose} className="px-6 py-2 rounded-full bg-[#F5F0E8]/10 hover:bg-[#F5F0E8]/20 text-white font-medium transition-colors" data-id="element-51">
            Cerrar
          </button>
        </div>
      </motion.div>
    </div>;
}
const privacyContent = <>
    <p data-id="element-52">
      <strong data-id="element-53">Última actualización: {new Date().toLocaleDateString()}</strong>
    </p>
    <p data-id="element-54">
      En "Dormí Hoy", respetamos tu privacidad y nos comprometemos a proteger
      los datos personales que nos compartas. Esta política explica cómo
      manejamos tu información.
    </p>

    <h4 className="text-[#D4A574] font-bold text-lg mt-4 mb-2" data-id="element-55">
      1. Información que recolectamos
    </h4>
    <p data-id="element-56">
      Solo recolectamos la información necesaria para procesar tu compra y
      entregarte el producto digital (ebook y materiales). Esto incluye tu
      nombre y dirección de correo electrónico al momento del pago. No
      almacenamos datos de tarjetas de crédito; estos son procesados de forma
      segura por nuestro proveedor de pagos.
    </p>

    <h4 className="text-[#D4A574] font-bold text-lg mt-4 mb-2" data-id="element-57">
      2. Uso de la información
    </h4>
    <p data-id="element-58">
      Usamos tu email exclusivamente para:
      <ul className="list-disc pl-5 mt-2 space-y-1" data-id="element-59">
        <li data-id="element-60">Enviarte el enlace de descarga del producto.</li>
        <li data-id="element-61">
          Enviarte actualizaciones importantes sobre el producto si fuera
          necesario.
        </li>
        <li data-id="element-62">Responder a tus consultas de soporte.</li>
      </ul>
    </p>

    <h4 className="text-[#D4A574] font-bold text-lg mt-4 mb-2" data-id="element-63">
      3. Protección de datos
    </h4>
    <p data-id="element-64">
      No vendemos, alquilamos ni compartimos tu información con terceros con
      fines comerciales. Tus datos están resguardados bajo estándares de
      seguridad habituales en la industria.
    </p>

    <h4 className="text-[#D4A574] font-bold text-lg mt-4 mb-2" data-id="element-65">4. Cookies</h4>
    <p data-id="element-66">
      Utilizamos cookies esenciales para el funcionamiento del sitio y el
      proceso de compra. No utilizamos cookies de rastreo invasivas.
    </p>
  </>;
const termsContent = <>
    <p data-id="element-67">
      <strong data-id="element-68">Última actualización: {new Date().toLocaleDateString()}</strong>
    </p>
    <p data-id="element-69">
      Al adquirir "Dormí Hoy", aceptás los siguientes términos y condiciones.
      Por favor, leelos con atención.
    </p>

    <h4 className="text-[#D4A574] font-bold text-lg mt-4 mb-2" data-id="element-70">
      1. Uso del producto
    </h4>
    <p data-id="element-71">
      "Dormí Hoy" es un producto digital (ebook y materiales complementarios)
      para uso personal y familiar. Queda estrictamente prohibida su
      distribución, reventa, copia o difusión pública sin autorización expresa.
      El material está protegido por derechos de autor.
    </p>

    <h4 className="text-[#D4A574] font-bold text-lg mt-4 mb-2" data-id="element-72">
      2. Naturaleza del servicio
    </h4>
    <p data-id="element-73">
      Este producto es una guía educativa basada en estrategias de sueño
      infantil. No constituye asesoramiento médico ni psicológico profesional.
      Si tu hijo presenta problemas de salud, ronquidos, apneas o ansiedad
      severa, consultá siempre con tu pediatra antes de aplicar cualquier
      método.
    </p>

    <h4 className="text-[#D4A574] font-bold text-lg mt-4 mb-2" data-id="element-74">
      3. Política de reembolso
    </h4>
    <p data-id="element-75">
      Debido a la naturaleza digital del producto (descarga inmediata), no
      ofrecemos reembolsos una vez que el material ha sido enviado, salvo en
      casos de error técnico comprobable que impida el acceso al archivo.
    </p>

    <h4 className="text-[#D4A574] font-bold text-lg mt-4 mb-2" data-id="element-76">
      4. Responsabilidad
    </h4>
    <p data-id="element-77">
      El autor no se hace responsable por la interpretación o aplicación
      incorrecta de la información contenida en la guía. Los resultados pueden
      variar según cada familia y la consistencia en la aplicación del método.
    </p>
  </>;